import { useState } from 'react'
import { submitContactForm } from '../utils/submitContactForm'
import {
  closeFormAlert,
  showFormError,
  showFormLoading,
  showFormSuccess,
  showFormValidationError,
} from '../utils/showFormAlert'

const quoteInitialState = {
  name: '',
  phone: '',
  email: '',
  product: [],
  quantity: '',
  grade: '',
  packing: '',
  deliveryLocation: '',
  message: '',
}

const inquiryInitialState = {
  name: '',
  phone: '',
  email: '',
  subject: '',
  message: '',
}

function validateQuoteForm(formData) {
  const errors = {}

  if (!formData.name.trim()) {
    errors.name = 'Please enter your name.'
  }

  if (!formData.phone.trim()) {
    errors.phone = 'Please enter your phone number.'
  } else if (!/^[+]?[\d\s()-]{8,18}$/.test(formData.phone.trim())) {
    errors.phone = 'Please enter a valid phone number.'
  }

  if (!formData.email.trim()) {
    errors.email = 'Please enter your email address.'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
    errors.email = 'Please enter a valid email address.'
  }

  if (!Array.isArray(formData.product) || formData.product.length === 0) {
    errors.product = 'Please select at least one product.'
  }

  if (!formData.quantity?.trim()) {
    errors.quantity = 'Please enter the required quantity.'
  }

  if (!formData.deliveryLocation?.trim()) {
    errors.deliveryLocation = 'Please enter the delivery location.'
  }

  return errors
}

function validateInquiryForm(formData) {
  const errors = {}

  if (!formData.name.trim()) {
    errors.name = 'Please enter your name.'
  }

  if (!formData.phone.trim()) {
    errors.phone = 'Please enter your phone number.'
  } else if (!/^[+]?[\d\s()-]{8,18}$/.test(formData.phone.trim())) {
    errors.phone = 'Please enter a valid phone number.'
  }

  if (!formData.email.trim()) {
    errors.email = 'Please enter your email address.'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
    errors.email = 'Please enter a valid email address.'
  }

  if (!formData.message.trim()) {
    errors.message = 'Please enter your inquiry message.'
  }

  return errors
}

const formConfig = {
  quote: {
    initialState: quoteInitialState,
    validate: validateQuoteForm,
    successMessage:
      'Thank you. Your quote request has been sent and our team will share pricing details shortly.',
    errorMessage: 'Unable to send your quote request right now. Please call or email us directly.',
  },
  inquiry: {
    initialState: inquiryInitialState,
    validate: validateInquiryForm,
    successMessage:
      'Thank you. Your inquiry has been received and our team will respond shortly.',
    errorMessage: 'Unable to send your inquiry right now. Please call or email us directly.',
  },
}

export function useContactForm(formType = 'inquiry') {
  const config = formConfig[formType] || formConfig.inquiry
  const [formData, setFormData] = useState(config.initialState)
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (event) => {
    const { name, value } = event.target

    setFormData((current) => ({ ...current, [name]: value }))

    if (errors[name]) {
      setErrors((current) => {
        const next = { ...current }
        delete next[name]
        return next
      })
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    const validationErrors = config.validate(formData)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      showFormValidationError('Please fix the highlighted fields and try again.')
      return
    }

    setIsSubmitting(true)
    setErrors({})
    showFormLoading()

    try {
      await submitContactForm(formData, formType)
      closeFormAlert()
      setFormData(config.initialState)
      await showFormSuccess(config.successMessage)
    } catch (error) {
      closeFormAlert()
      await showFormError(error?.message || config.errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    formData,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit,
  }
}
